/* =============================================================================
   Studio 47° Pilates — global helpers
   Exposed as window.S47. Pure utilities — no DOM mutation here.
   Ports lib/timezone.ts + portions of lib/clientStore.ts (read side).
   ============================================================================= */
(function () {
  'use strict';

  var TZ = (window.__s47 && window.__s47.settings && window.__s47.settings.tz) || 'Asia/Karachi';

  /**
   * Returns the weekday (1=Mon ... 7=Sun) for an ISO timestamp in Asia/Karachi.
   * Uses Intl formatToParts to avoid the parseDate() round-trip bug in the
   * original lib/mockData.ts (new Date(d.toLocaleString(...)) mis-reads under
   * non-en locales).
   */
  function karachiWeekday(iso) {
    var d = iso instanceof Date ? iso : new Date(iso);
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: TZ,
      weekday: 'short'
    }).formatToParts(d);
    var wd = parts.find(function (p) { return p.type === 'weekday'; }).value;
    var map = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };
    return map[wd];
  }

  /**
   * Day-key (YYYY-MM-DD) in Asia/Karachi. Useful for deduping by date.
   */
  function dayKey(iso) {
    var d = iso instanceof Date ? iso : new Date(iso);
    return d.toLocaleDateString('en-CA', { timeZone: TZ });
  }

  /** PKR money formatter — "Rs. 4,500" with no decimals. */
  function fmtPkr(amount) {
    var n = Number(amount);
    if (!isFinite(n)) return 'Rs. 0';
    return 'Rs. ' + Math.round(n).toLocaleString('en-PK');
  }

  /** HH:MM in Asia/Karachi for an ISO string. */
  function fmtTime(iso) {
    return new Date(iso).toLocaleTimeString('en-PK', {
      timeZone: TZ, hour: 'numeric', minute: '2-digit'
    });
  }

  /** Short weekday name in Asia/Karachi. */
  function fmtDay(iso) {
    return new Date(iso).toLocaleDateString('en-PK', {
      timeZone: TZ, weekday: 'short'
    });
  }

  /** Long "Monday, March 14" style date. */
  function fmtDate(iso) {
    return new Date(iso).toLocaleDateString('en-PK', {
      timeZone: TZ, weekday: 'long', month: 'long', day: 'numeric'
    });
  }

  /* -------------------- Credit ledger helpers (read-only) -------------------- */

  /** Return customer credits in `active` state whose expires_at is still in the future. */
  function activeCredits() {
    var now = Date.now();
    return (window.__s47.credits || []).filter(function (c) {
      return c.state === 'active' && new Date(c.expires_at).getTime() > now;
    });
  }

  /**
   * Credits the customer can use on a given Karachi weekday.
   * Sorted by earliest expiry first — matches redemption order.
   */
  function usableOn(weekday) {
    return activeCredits()
      .filter(function (c) { return (c.allowed_weekdays || []).indexOf(weekday) !== -1; })
      .sort(function (a, b) {
        return new Date(a.expires_at) - new Date(b.expires_at);
      });
  }

  /** Has the customer booked this Sesami appointment already? */
  function hasBookedSession(sessionId) {
    return (window.__s47.bookings || []).some(function (b) {
      return b.sesami_appointment_id === sessionId && b.state === 'confirmed';
    });
  }

  /* -------------------- Toasts -------------------- */

  var toastStack;
  function ensureToastStack() {
    if (toastStack) return toastStack;
    toastStack = document.createElement('div');
    toastStack.className = 's47-toast-stack';
    toastStack.setAttribute('aria-live', 'polite');
    toastStack.setAttribute('role', 'status');
    document.body.appendChild(toastStack);
    return toastStack;
  }
  function toast(message, ms) {
    ms = ms || 2400;
    var stack = ensureToastStack();
    var el = document.createElement('div');
    el.className = 's47-toast';
    el.textContent = message;
    stack.appendChild(el);
    setTimeout(function () {
      el.style.opacity = '0';
      el.style.transition = 'opacity 200ms ease';
    }, ms - 200);
    setTimeout(function () { el.remove(); }, ms);
  }

  /* -------------------- Cross-tab seat sync (ports lib/seatStore.ts) -------------------- */

  var SEAT_CHANNEL = 's47-seats';
  var channel = null;
  if (typeof BroadcastChannel !== 'undefined') {
    try { channel = new BroadcastChannel(SEAT_CHANNEL); } catch (e) { channel = null; }
  }

  var seatListeners = [];
  if (channel) {
    channel.addEventListener('message', function (e) {
      seatListeners.forEach(function (fn) { try { fn(e.data); } catch (_) {} });
    });
  }

  function publishSeatChange(payload) {
    if (!channel) return;
    try { channel.postMessage(payload); } catch (_) {}
  }
  function onSeatChange(fn) {
    seatListeners.push(fn);
    return function () {
      seatListeners = seatListeners.filter(function (f) { return f !== fn; });
    };
  }

  /* -------------------- Public API -------------------- */

  window.S47 = {
    tz: TZ,
    karachiWeekday: karachiWeekday,
    dayKey: dayKey,
    fmtPkr: fmtPkr,
    fmtTime: fmtTime,
    fmtDay: fmtDay,
    fmtDate: fmtDate,
    activeCredits: activeCredits,
    usableOn: usableOn,
    hasBookedSession: hasBookedSession,
    toast: toast,
    publishSeatChange: publishSeatChange,
    onSeatChange: onSeatChange
  };
})();
